import { isEmpty, isEqual } from 'lodash-es'
import { useCallback } from 'react'
import {
  useFetchDirectoryKolBasicInfoQuery,
  useFetchKolContactInfosQuery,
} from '@/api/workspace-api/directory-api'
import useKolInfo from '@/components/kol/hooks/use-kol-info'
import { useSelector } from '@/hooks/use-selector'
import {
  BasicInfo,
  ContactInfo,
  ContactType,
} from '@/types/schema/directory/kol-management'
import { ampli } from '@/utils/ampli'
import removeEmpty from '@/utils/remove-empty'

const contactFieldNameMap: Record<ContactType, string> = {
  email: 'Email',
  phone: 'Phone',
  line: 'Line',
  'we-chat': 'WeChat',
  whatsapp: 'WhatsApp',
  qq: 'QQ',
}

const basicInfoFieldNameMap: Record<keyof BasicInfo, string> = {
  birthday: 'Birthday',
  profession: 'Occupation',
  address: 'Residence',
  customized_labels: 'Custom Tags',
}

type UseKolContactInfoProps = (kolId: string) => {
  basicInfo?: BasicInfo
  contactInfos?: ContactInfo[]
  isLoading: boolean
  isFetchingBasicInfo: boolean
  isFetchingContactInfo: boolean
  handleEditContactEvent: (
    modifiedField: ContactType,
    isDeleteOrEmpty: boolean,
  ) => void
  handleEditBasicInfoEvent: (updateValues: BasicInfo) => void
}

export const useKolContactInfo: UseKolContactInfoProps = (kolId) => {
  const from = useSelector((state) => state.kolCooperationDetailDrawer.data.at)

  const {
    data: kolBasicInfoResponse,
    isFetching: isFetchingBasicInfo,
    isLoading: isLoadingBasicInfo,
  } = useFetchDirectoryKolBasicInfoQuery({
    kolId,
  })

  const {
    data: kolContactInfosResponse,
    isFetching: isFetchingContactInfo,
    isLoading: isLoadingContactInfo,
  } = useFetchKolContactInfosQuery({ kolId })

  const { kolInfo } = useKolInfo(kolId)
  const basicInfo = kolBasicInfoResponse?.data.basic_info
  const contactInfos = kolContactInfosResponse?.data.contact_infos

  const handleEditContactEvent = useCallback(
    (modifiedField: ContactType, isDeleteOrEmpty: boolean): void => {
      if (!basicInfo || !contactInfos) {
        return
      }

      const modifiedFieldName = contactFieldNameMap[modifiedField]
      const currentNames = contactInfos.map(
        (contactInfo) => contactFieldNameMap[contactInfo.type],
      )

      const fieldNames = [
        ...Object.keys(removeEmpty(basicInfo)),
        ...(isDeleteOrEmpty
          ? currentNames.filter(
              (_, index) => index !== currentNames.indexOf(modifiedFieldName),
            )
          : [...currentNames, modifiedFieldName]),
      ]

      ampli.editDrawerContactInformation({
        kolId,
        kolName: kolInfo?.name,
        fieldNames,
        modifiedFieldNames: [modifiedFieldName],
        count: fieldNames.length,
        from,
      })
    },
    [basicInfo, kolInfo, kolId, contactInfos],
  )

  const handleEditBasicInfoEvent = useCallback(
    (updateValues: BasicInfo): void => {
      if (!basicInfo || !contactInfos) {
        return
      }

      const fieldNames = [
        ...Object.keys(removeEmpty(updateValues)).map(
          (key) => basicInfoFieldNameMap[key],
        ),
        ...contactInfos
          .filter((contactInfo) => !isEmpty(contactInfo.value))
          .map((contactInfo) => {
            return contactFieldNameMap[contactInfo.type]
          }),
      ]

      ampli.editDrawerContactInformation({
        kolId,
        kolName: kolInfo?.name,
        modifiedFieldNames: ((): string[] => {
          const result: string[] = []
          for (const key of Object.keys(updateValues)) {
            if (!isEqual(updateValues[key], basicInfo[key])) {
              result.push(basicInfoFieldNameMap[key])
            }
          }
          return result
        })(),
        fieldNames,
        count: fieldNames.length,
        from,
      })

      if (
        !isEqual(updateValues.customized_labels, basicInfo.customized_labels)
      ) {
        ampli.editCustomTags({
          kolId,
          kolName: kolInfo?.name,
          tags: updateValues.customized_labels.map((tag) => tag.name),
          isClickedFromDrawer: true,
          from,
        })
      }
    },
    [basicInfo, kolInfo, kolId, contactInfos],
  )

  return {
    basicInfo,
    contactInfos,
    isLoading: isLoadingBasicInfo || isLoadingContactInfo,
    isFetchingBasicInfo,
    isFetchingContactInfo,
    handleEditContactEvent,
    handleEditBasicInfoEvent,
  }
}
